import type {
	ComponentProps,
	ComponentType,
	LazyExoticComponent,
	ReactNode,
} from 'react';
import { createElement, lazy, useState } from 'react';

/**
 *	App builds ship as ~96 content-hashed chunks so Capgo delta updates only have
 *	to re-download what changed. The trade-off is that navigation and boot now
 *	depend on individual files resolving, so a chunk that fails to load has to
 *	recover instead of leaving a blank screen.
 */

/**
 *	The bundle version that already spent its recovery reload. Keyed by version
 *	and not by session, because a tab outlives several deploys and only the first
 *	would ever recover.
 */
const RELOAD_FLAG = 'chunkReloadAttempted';

/** Stands in for the version on a build that shipped without one. */
const UNKNOWN_BUNDLE = 'unknown';

const RETRY_ATTEMPTS = 2;
const RETRY_DELAY_MS = 250;

/**
 *	What a JavaScript engine says when it starts parsing a script and finds `<`
 *	on the first line — Chromium and Safari first, then Firefox.
 *
 *	Matched in full rather than on the leading `<` so a JSON.parse of an HTML
 *	error page, which reads `Unexpected token '<', "<!doctype "... is not valid
 *	JSON`, does not read as a chunk failure and reload the page under the user.
 */
const HTML_PARSED_AS_SCRIPT = [
	"Unexpected token '<'",
	"expected expression, got '<'",
];

type BundleVersion = () => string | undefined;

type UnrecoverableChunkErrorHandler = (reason: unknown) => void;

let bundleVersion: BundleVersion | null = null;

let unrecoverableChunkErrorHandler: UnrecoverableChunkErrorHandler | null =
	null;

export function isChunkLoadError(reason: unknown): boolean {
	if (!reason) {
		return false;
	}

	// Webpack names its own rejection; the message check covers the CSS loader,
	// which rejects with a plain Error.
	const { name, message } = reason as Partial<Error>;

	if (
		name === 'ChunkLoadError' ||
		/Loading (CSS )?chunk .* failed/i.test(message ?? '')
	) {
		return true;
	}

	// A cache or CDN we do not control can answer a chunk that a deploy removed
	// with 200 and index.html instead of a 404. The browser then parses HTML as
	// JavaScript, so the request succeeds and webpack never raises
	// ChunkLoadError — the failure only ever surfaces as a parse error.
	return (
		name === 'SyntaxError' && HTML_PARSED_AS_SCRIPT.includes(message ?? '')
	);
}

function wait(ms: number): Promise<void> {
	return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 *	Reloads once when a chunk fails to load, which is the only thing that
 *	recovers a bundle swapped underneath a live page — the reload picks up the
 *	new bundle's index.html and its chunk names.
 *
 *	Deliberately capped at a single attempt per bundle. If the reload comes back
 *	to the same version we want to stop and stay broken, because then
 *	CapacitorUpdater.notifyAppReady() never fires and Capgo rolls the bundle
 *	back. Reloading forever would defeat that. Once the bundle has been acked
 *	that rollback is gone, so a spent reload hands over to the handler set with
 *	onUnrecoverableChunkError instead, unless the failed chunk has a fallback
 *	that keeps the view working.
 */
function recover(reason: unknown, hasFallback = false): void {
	if (!bundleVersion || !isChunkLoadError(reason)) {
		return;
	}

	// webpack bakes TS.version into index.html, and an OTA bundle ships its
	// own, so a match means the reload came back to the same broken bundle.
	const bundle = bundleVersion() || UNKNOWN_BUNDLE;

	if (sessionStorage.getItem(RELOAD_FLAG) === bundle) {
		if (!hasFallback) {
			unrecoverableChunkErrorHandler?.(reason);
		}

		return;
	}

	sessionStorage.setItem(RELOAD_FLAG, bundle);
	window.location.reload();
}

/**
 *	Retries a dynamic import a couple of times before giving up, then hands the
 *	result to React.lazy.
 *
 *	@warn The retry has to happen inside the factory. React.lazy caches a
 *	rejected payload permanently, so re-rendering a failed lazy component never
 *	refetches it — an error boundary "retry" button that just resets state will
 *	re-reject instantly. Recovery past this point means a reload, not a retry.
 *
 *	React.lazy also handles the rejection itself, so it never reaches the
 *	window listeners in initChunkRecovery. A chunk that is still failing after
 *	the retries is handed to the recovery here instead.
 *
 *	A `fallback` is a component already in the bundle that the lazy one
 *	becomes if its chunk still fails to load after the retries, so the view
 *	keeps working instead of crashing. The failure still goes to the recovery
 *	first, as a tab left open across a deploy only gets the chunk by reloading
 *	into the new build: the fallback shows until the page unloads, and for good
 *	once the bundle has spent its reload. React.lazy keeps it until a reload.
 */
export function lazyRetry<T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>,
	fallback?: T
): LazyExoticComponent<T> {
	return lazy(async () => {
		for (let attempt = 0; ; attempt++) {
			try {
				return await factory();
			} catch (error) {
				if (!isChunkLoadError(error)) {
					throw error;
				}

				if (attempt >= RETRY_ATTEMPTS) {
					recover(error, !!fallback);

					if (fallback) {
						return { default: fallback };
					}

					throw error;
				}

				await wait(RETRY_DELAY_MS * (attempt + 1));
			}
		}
	});
}

export interface Preloadable<T extends ComponentType<any>> {
	/** Renders the chunk's component, suspending only while it isn't there yet. */
	Component: ComponentType<ComponentProps<T>>;
	/**
	 *	Fetches the chunk ahead of its first render. A failure is left for that
	 *	render to retry, with `lazyRetry`'s retries and fallback, rather than
	 *	kept: a fetch made while idle on a flaky connection must not decide
	 *	what the component is once someone asks for it.
	 */
	preload: () => void;
}

/**
 *	`lazyRetry` for a chunk worth fetching before it is needed, such as a
 *	page someone is likely to open next. Once the chunk has arrived, a
 *	mount renders its component straight away: a `React.lazy` would still
 *	suspend for the moment it takes to read even a settled import, and show
 *	its Suspense fallback for that moment.
 */
export function lazyPreload<T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>,
	fallback?: T
): Preloadable<T> {
	type Props = ComponentProps<T>;

	let loaded: T | undefined;

	const load = async (): Promise<{ default: T }> => {
		const module = await factory();

		loaded = module.default;

		return module;
	};

	const Lazy = lazyRetry(load, fallback);

	// Which of the two a mount renders is settled once, so the chunk arriving
	// while it is mounted never swaps its type and remounts what's under it.
	function Component(props: Props): ReactNode {
		const [Loaded] = useState(
			() => (loaded ?? Lazy) as unknown as ComponentType<Props>
		);

		return createElement(Loaded, props);
	}

	const preload = (): void => {
		if (!loaded) {
			load().catch((): void => undefined);
		}
	};

	return { Component, preload };
}

export function initChunkRecovery(version: BundleVersion): void {
	bundleVersion = version;

	window.addEventListener('unhandledrejection', (event) => {
		recover(event.reason);
	});

	window.addEventListener('error', (event) => {
		recover(event.error);
	});
}

export function onUnrecoverableChunkError(
	handler: UnrecoverableChunkErrorHandler
): void {
	unrecoverableChunkErrorHandler = handler;
}
